import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dataService } from '../services/data-service';
import { setFinancialsInstanceId, getAuthConfig } from '../lib/auth-fetch';

interface IntegrationSettings {
  integrationType: string;
  instanceId?: string;
  instanceName?: string;
  enabled: boolean;
}

interface IntegrationContextType {
  settings: IntegrationSettings | null;
  isLoading: boolean;
  error: string | null;
  setSelectedInstance: (integrationType: string, instanceId: string, instanceName: string, enabled: boolean) => Promise<void>;
  clearSelectedInstance: (integrationType: string) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

export function IntegrationProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [settings, setSettings] = useState<IntegrationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedInstanceIdRef = useRef<string | undefined>(undefined);
  const hasFetchedWithValidInstanceIdRef = useRef<boolean>(false);

    const refreshSettings = useCallback(async () => {
      const authConfig = getAuthConfig();
      const currentInstanceId = authConfig.instanceId;
      
      // Track the instanceId we're fetching with
      lastFetchedInstanceIdRef.current = currentInstanceId;
      
      // Track if we've fetched with a valid (non-default) instanceId
      if (currentInstanceId && currentInstanceId !== 'default-instance') {
        hasFetchedWithValidInstanceIdRef.current = true;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        const result = await dataService.getIntegrationSettings();
        if (result) {
          setSettings({
            integrationType: result.integrationType,
            instanceId: result.instanceId,
            instanceName: result.instanceName,
            enabled: result.enabled,
          });
          // Update financials instance ID in auth config if this is the financials integration
          if (result.integrationType === 'access-financials' && result.enabled && result.instanceId) {
            setFinancialsInstanceId(result.instanceId);
          } else {
            setFinancialsInstanceId();
          }
        } else {
          setSettings(null);
          setFinancialsInstanceId();
        }
      } catch (err) {
        console.error('Failed to load integration settings:', err);
        setError('Failed to load integration settings');
        setSettings(null);
        setFinancialsInstanceId();
      } finally {
        setIsLoading(false);
      }
    }, []);

  // Initial fetch on mount
  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);
  
  // Poll for instanceId changes - if instanceId becomes available after initial fetch,
  // re-fetch the settings. This handles the case where auth is not ready on first render.
  useEffect(() => {
    // Only poll if we haven't successfully fetched with a valid instanceId yet
    if (hasFetchedWithValidInstanceIdRef.current) {
      return;
    }
    
    const checkInstanceId = () => {
      const authConfig = getAuthConfig();
      const currentInstanceId = authConfig.instanceId;
      
      // If instanceId has changed to a valid value and we haven't fetched with it yet
      if (currentInstanceId && 
          currentInstanceId !== 'default-instance' && 
          currentInstanceId !== lastFetchedInstanceIdRef.current) {
        refreshSettings();
      }
    };
    
    // Check periodically for instanceId changes
    const intervalId = setInterval(checkInstanceId, 500);
    
    // Also check immediately in case instanceId is already available
    checkInstanceId();
    
    return () => clearInterval(intervalId);
  }, [refreshSettings]);

    const setSelectedInstance = useCallback(async (
      integrationType: string,
      instanceId: string,
      instanceName: string,
      enabled: boolean
    ) => {
      try {
        setError(null);
        const result = await dataService.saveIntegrationSettings({
          integrationType,
          instanceId,
          instanceName,
          enabled,
        });
        setSettings({
          integrationType: result.integrationType,
          instanceId: result.instanceId,
          instanceName: result.instanceName,
          enabled: result.enabled,
        });
        // Update financials instance ID in auth config if this is the financials integration
        if (result.integrationType === 'access-financials' && result.enabled && result.instanceId) {
          setFinancialsInstanceId(result.instanceId);
        } else {
          setFinancialsInstanceId();
        }
      } catch (err) {
        console.error('Failed to save integration settings:', err);
        setError('Failed to save integration settings');
        throw err;
      }
    }, []);

    const clearSelectedInstance = useCallback(async (integrationType: string) => {
      try {
        setError(null);
        await dataService.saveIntegrationSettings({
          integrationType,
          instanceId: undefined,
          instanceName: undefined,
          enabled: false,
        });
        setSettings(null);
        // Clear financials instance ID in auth config
        if (integrationType === 'access-financials') {
          setFinancialsInstanceId();
        }
      } catch (err) {
        console.error('Failed to clear integration settings:', err);
        setError('Failed to clear integration settings');
        throw err;
      }
    }, []);

  const value = useMemo<IntegrationContextType>(() => ({
    settings,
    isLoading,
    error,
    setSelectedInstance,
    clearSelectedInstance,
    refreshSettings,
  }), [settings, isLoading, error, setSelectedInstance, clearSelectedInstance, refreshSettings]);

  return <IntegrationContext.Provider value={value}>{children}</IntegrationContext.Provider>;
}

export function useIntegration(): IntegrationContextType {
  const context = useContext(IntegrationContext);
  if (context === undefined) {
    throw new Error('useIntegration must be used within an IntegrationProvider');
  }
  return context;
}
